import { Component, OnInit, signal, computed, ChangeDetectionStrategy, inject, DestroyRef, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { RouterModule } from '@angular/router';

export interface AnnouncementItem {
  badge: string;
  text: string;
  mobileText?: string;
  actionText?: string;
  actionUrl?: string;
}

@Component({
  selector: 'app-elite-announcement-bar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="relative w-full bg-gradient-to-r from-[#150220] via-[#110118] to-[#150220] border-t border-b border-champagne-gold/40 text-silk-white py-2 px-3 sm:px-6 overflow-hidden shadow-xl">
      
      <!-- Capa 1: Aura radial de oro fundido en movimiento -->
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-champagne-gold/20 via-transparent to-transparent pointer-events-none animate-pulse"></div>
      
      <!-- Capa 2: Brillo especular superior -->
      <div class="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-champagne-gold to-transparent opacity-70"></div>

      <div class="max-w-7xl mx-auto flex items-center justify-between text-[10px] sm:text-[12px] tracking-wider relative z-10">
        
        <!-- Carril Izquierdo: Badge con relieve dorado y sombra -->
        <div class="flex items-center shrink-0">
          <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-gradient-to-r from-champagne-gold/30 to-champagne-gold/10 text-champagne-gold font-serif font-bold uppercase tracking-widest text-[9px] sm:text-[11px] border border-champagne-gold/60 shadow-[0_0_12px_rgba(197,160,89,0.3)] leading-none">
            {{ currentAnnouncement().badge }}
          </span>
        </div>

        <!-- Carril Central: Texto dinámico con brillo tridimensional y fade suave -->
        <div class="flex-1 px-3 flex items-center justify-center text-center truncate">
          <span class="font-sans font-normal text-silk-white drop-shadow-[0_1px_3px_rgba(197,160,89,0.35)] transition-all duration-700 ease-in-out truncate inline-block max-w-full leading-normal">
            <span class="sm:hidden">{{ currentAnnouncement().mobileText || currentAnnouncement().text }}</span>
            <span class="hidden sm:inline">{{ currentAnnouncement().text }}</span>
          </span>
        </div> 

        <!-- Carril Derecho: CTA Brillante de Alta Gama -->
        <div class="flex items-center shrink-0">
          <a [routerLink]="currentAnnouncement().actionUrl || '/tienda'" 
             class="inline-flex items-center px-2.5 py-1 rounded-md bg-champagne-gold/10 hover:bg-champagne-gold/25 border border-champagne-gold/50 font-medium text-champagne-gold transition-all duration-300 shadow-sm text-[10px] sm:text-[11px] whitespace-nowrap leading-none">
            {{ currentAnnouncement().actionText || 'ver' }} ;
          </a>
        </div>

      </div>

      <!-- Indicador sutil de rotación por puntos en la base inferior -->
      <div class="absolute bottom-0.5 inset-x-0 flex justify-center space-x-1 opacity-60">
        @for (item of announcements(); track $index) {
          <span class="h-[2px] rounded-full transition-all duration-500"
                [class.w-3]="currentIndex() === $index"
                [class.w-1]="currentIndex() !== $index"
                [class.bg-champagne-gold]="currentIndex() === $index"
                [class.bg-champagne-gold/30]="currentIndex() !== $index">
          </span>
        }
      </div>
    </div>
  `
})
export class EliteAnnouncementBarComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID); 

  announcements = signal<AnnouncementItem[]>([
    {
      badge: 'Envio Gratis',
      text: 'Pago Contra Entrega disponible — Recibe y paga en casa con total tranquilidad.',
      mobileText: 'Pago Contra Entrega en Bogotá 🚚 Paga al recibir',
      actionText: 'ver',
      actionUrl: '/tienda'  
    }, 
    {
      badge: 'Producto Original', 
      text: 'Descubre el formato Vial Portátil de Lujo (5ml y 10ml) y evita compras a ciegas.',
      mobileText: 'Pruébalo en Decant de 5ml y 10ml ✨ 100% Originales',
      actionText: 'más',
      actionUrl: '/tienda' 
    },
    {
      badge: 'Compra Segura',
      text: 'Despachos garantizados a todo el país a través de nuestra alianza logística.',
      mobileText: 'Envíos seguros a todo Colombia 📦',
      actionText: 'info',
      actionUrl: '/tienda'
    }  
  ]);

  currentIndex = signal(0);
  currentAnnouncement = computed(() => this.announcements()[this.currentIndex()]);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      interval(6000)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.currentIndex.update(index => (index + 1) % this.announcements().length);
        }); 
    }
  }
}
